import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import { mapApiWord, type ApiWord } from './words'
import type { WordWithTags } from './words'
import type {
  Dictionary,
  DictionaryFilters,
  CreateDictionaryData,
  UpdateDictionaryData,
  ImportWordsResult,
  PaginatedResponse,
} from '@/types'

const BASE_URL = API_ENDPOINTS.DICTIONARIES

interface ApiResponse<T> {
  success?: boolean
  message?: string
  data?: T
}

interface DictionariesListResponse {
  data?: Dictionary[]
  meta?: {
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
  }
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
}

interface DictionaryWordsResponse {
  data?: ApiWord[]
  meta?: {
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
  }
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
}

export interface GetDictionaryWordsParams {
  page?: number
  per_page?: number
  search?: string
  tags?: number[]
  part_of_speech?: string
  without_translation?: boolean
}

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value !== undefined && value !== null) {
      if (typeof value === 'boolean') {
        searchParams.append(key, value ? 'true' : 'false')
      } else if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })
  return searchParams.toString()
}

export const dictionariesApi = {
  getDictionaries: async (
    params?: DictionaryFilters
  ): Promise<PaginatedResponse<Dictionary>> => {
    const query = params ? `?${buildQueryString(params as Record<string, unknown>)}` : ''
    const response = await apiClient.get<Dictionary[] | DictionariesListResponse>(
      `${BASE_URL}${query}`
    )

    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: response.data.length,
      }
    }

    const raw = response.data as DictionariesListResponse
    if (raw?.data && Array.isArray(raw.data)) {
      const meta = raw.meta || {}
      return {
        data: raw.data,
        current_page: meta.current_page ?? raw.current_page ?? 1,
        last_page: meta.last_page ?? raw.last_page ?? 1,
        per_page: meta.per_page ?? raw.per_page ?? 20,
        total: meta.total ?? raw.total ?? 0,
      }
    }

    throw new Error(
      (raw as { message?: string })?.message || 'Ошибка загрузки словарей'
    )
  },

  getDictionary: async (id: number): Promise<Dictionary> => {
    const response = await apiClient.get<ApiResponse<Dictionary>>(
      `${BASE_URL}/${id}`
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(
      response.data?.message || 'Ошибка загрузки словаря'
    )
  },

  createDictionary: async (body: CreateDictionaryData): Promise<Dictionary> => {
    const response = await apiClient.post<ApiResponse<Dictionary>>(
      BASE_URL,
      body
    )

    if (response.data?.data) {
      return response.data.data
    }

    const err = response.data as { message?: string }
    throw new Error(err?.message || 'Ошибка создания словаря')
  },

  updateDictionary: async (
    id: number,
    body: UpdateDictionaryData
  ): Promise<Dictionary> => {
    const response = await apiClient.put<ApiResponse<Dictionary>>(
      `${BASE_URL}/${id}`,
      body
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(
      response.data?.message || 'Ошибка обновления словаря'
    )
  },

  deleteDictionary: async (id: number): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `${BASE_URL}/${id}`
    )

    if (response.data?.success === false) {
      throw new Error(
        (response.data as { message?: string })?.message || 'Ошибка удаления словаря'
      )
    }
  },

  getDictionaryWords: async (
    id: number,
    params?: GetDictionaryWordsParams
  ): Promise<{
    data: WordWithTags[]
    current_page: number
    last_page: number
    per_page: number
    total: number
  }> => {
    const query = params ? `?${buildQueryString(params as Record<string, unknown>)}` : ''
    const response = await apiClient.get<DictionaryWordsResponse>(
      `${BASE_URL}/${id}/words${query}`
    )

    const raw = response.data
    if (!raw) {
      throw new Error('Ошибка загрузки слов словаря')
    }

    const items = raw.data ?? []
    const meta = raw.meta || {}
    return {
      data: items.map((w) => mapApiWord(w as ApiWord)),
      current_page: meta.current_page ?? raw.current_page ?? 1,
      last_page: meta.last_page ?? raw.last_page ?? 1,
      per_page: meta.per_page ?? raw.per_page ?? 50,
      total: meta.total ?? raw.total ?? 0,
    }
  },

  attachWordsToDictionary: async (
    id: number,
    wordIds: number[]
  ): Promise<void> => {
    const response = await apiClient.post<ApiResponse<unknown>>(
      `${BASE_URL}/${id}/attach-words`,
      { word_ids: wordIds }
    )

    if (response.data?.success === false) {
      throw new Error(
        (response.data as { message?: string })?.message || 'Ошибка прикрепления слов'
      )
    }
  },

  detachWordsFromDictionary: async (
    id: number,
    wordIds: number[]
  ): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `${BASE_URL}/${id}/detach-words`,
      { data: { word_ids: wordIds } }
    )

    if (response.data?.success === false) {
      throw new Error(
        (response.data as { message?: string })?.message || 'Ошибка открепления слов'
      )
    }
  },

  importWordsFromDictionary: async (
    id: number,
    duplicateStrategy: 'skip' | 'update'
  ): Promise<ImportWordsResult> => {
    const response = await apiClient.post<ApiResponse<ImportWordsResult>>(
      `${BASE_URL}/${id}/import`,
      { duplicate_strategy: duplicateStrategy }
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(
      response.data?.message || 'Ошибка импорта слов'
    )
  },
}
